import { jwtDecode, JwtPayload } from "jwt-decode";
import { createContext, useCallback, useContext, useEffect, useReducer, useState } from "react";
import { flushSync } from "react-dom";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
    clearAuthStorage,
    getStoredFederation,
    OAUTH_FEDERATION_SESSION_KEY,
    POST_LOGOUT_RETURN_SESSION_KEY,
    promotePendingOAuthFederation,
    requestLogoutApi,
    STORAGE_KEYS,
} from "../services/logoutSession";
import { markOAuthLoginSession } from "../services/signupConversionSession";
import { SessionLogoutOverlay } from "../layout/MainLoader";
import { AnalyticsEvent, Tracker } from "../analytics";
import {
    clearPkceSession,
    consumeAuthorizationCodeCallback,
} from "../services/oauthPkce";
import { isCompleteRegisterClaim } from "../services/registrationComplete";

interface IAuthProviderProps {
    children: React.ReactNode;
}

interface IUserState {
    user_id: string;
    name: string;
    email: string;
}

type IdTokenClaims = JwtPayload & {
    user_id?: string;
    name?: string;
    email?: string;
    complete_register?: string | boolean;
};

interface IAuthState {
    isAuthenticated: boolean;
    needsCompleteRegister: boolean;
    isLoading: boolean;
    token: string;
    user: IUserState;
}

interface IAuthContext extends IAuthState {
    login: (token: string) => void;
    finishRegistration: (token: string, refreshToken?: string | null) => void;
    logout: () => Promise<void>;
    refreshingToken: boolean;
    isLoggingOut: boolean;
}

const AuthContext = createContext<IAuthContext | null>(null);

const emptyUser: IUserState = {
    user_id: "",
    name: "",
    email: "",
};

const initialState: IAuthState = {
    isAuthenticated: false,
    needsCompleteRegister: false,
    isLoading: true,
    token: "",
    user: emptyUser,
};

type AuthAction =
    | { type: "Login"; payload: { token: string; user: IUserState } }
    | { type: "PendingCompleteRegister"; payload: { token: string; user: IUserState } }
    | { type: "Logout"; payload: null };

const reducer = (state: IAuthState, action: AuthAction): IAuthState => {
    switch (action.type) {
        case "Login":
            return {
                ...state,
                isAuthenticated: true,
                needsCompleteRegister: false,
                token: action.payload.token,
                user: action.payload.user,
                isLoading: false,
            };
        case "PendingCompleteRegister":
            return {
                ...state,
                isAuthenticated: false,
                needsCompleteRegister: true,
                token: action.payload.token,
                user: action.payload.user,
                isLoading: false,
            };
        case "Logout":
            return {
                ...initialState,
                isLoading: false,
            };
        default:
            throw new Error(`Type ${(action as AuthAction).type} is not implemented.`);
    }
};

function userFromClaims(verified: IdTokenClaims): IUserState {
    return {
        user_id: verified.user_id ?? "",
        name: verified.name ?? (verified.email ? verified.email.split("@")[0]! : ""),
        email: verified.email ?? "",
    };
}

function assertValidIdToken(accessToken: string): IdTokenClaims {
    const verified: IdTokenClaims = jwtDecode(accessToken);

    if ((verified.exp ?? 0) * 1000 < Date.now()) {
        throw new Error("Token expirado");
    }

    if (verified.iss !== `${import.meta.env.VITE_COGNITO_URL}`) {
        throw new Error("Token inválido");
    }

    return verified;
}

async function assertJwksKid(accessToken: string): Promise<void> {
    const decodedToken = jwtDecode(accessToken, { header: true }) as { kid?: string };
    const keys = (
        await (
            await fetch(`${import.meta.env.VITE_COGNITO_URL}/.well-known/jwks.json`, {
                method: "GET",
            })
        ).json()
    ).keys as { kid: string }[];

    const key = keys.find((k) => k.kid === decodedToken.kid);
    if (!key) {
        throw new Error("Chave pública não encontrada.");
    }
}

const AuthProvider = ({ children }: IAuthProviderProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [refreshingToken, setRefreshingToken] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const queryClient = useQueryClient();

    const settleSession = useCallback(
        (accessToken: string, opts?: { oauthCallback?: boolean; oauthMethod?: "google" | "apple" | "password" }) => {
            const verified = assertValidIdToken(accessToken);
            const user = userFromClaims(verified);

            if (!user.user_id) {
                clearAuthStorage();
                clearPkceSession();
                throw new Error(
                    "Token sem user_id. Conclua o login novamente; se persistir, o mapeamento SSO no Cognito falhou.",
                );
            }

            if (!isCompleteRegisterClaim(verified.complete_register)) {
                dispatch({
                    type: "PendingCompleteRegister",
                    payload: { token: accessToken, user },
                });
                if (opts?.oauthCallback) {
                    markOAuthLoginSession();
                    Tracker.trackLogin("success", opts.oauthMethod === "apple" ? "apple" : "google");
                }
                return;
            }

            dispatch({ type: "Login", payload: { token: accessToken, user } });
            if (opts?.oauthCallback) {
                markOAuthLoginSession();
                Tracker.trackLogin("success", opts.oauthMethod === "apple" ? "apple" : "google");
            }
        },
        [],
    );

    useEffect(() => {
        const validateToken = async () => {
            let isOAuthCallback = false;
            let oauthMethod: "google" | "apple" = "google";

            try {
                // 1) Authorization Code + PKCE (fluxo atual do portal e do app)
                const pkceTokens = await consumeAuthorizationCodeCallback();
                if (pkceTokens) {
                    isOAuthCallback = true;
                    const pendingFed = sessionStorage.getItem(OAUTH_FEDERATION_SESSION_KEY);
                    oauthMethod = pendingFed === "apple" ? "apple" : "google";

                    localStorage.setItem(STORAGE_KEYS.TOKEN, pkceTokens.idToken);
                    localStorage.setItem(STORAGE_KEYS.REFRESH, pkceTokens.refreshToken);
                    promotePendingOAuthFederation();
                } else {
                    // 2) Legado: implicit grant no hash (compatibilidade)
                    const hash = window.location.hash;
                    const hashParams = new URLSearchParams(hash.replace("#", "?"));
                    if (hashParams.get("access_token") || hashParams.get("id_token")) {
                        isOAuthCallback = true;
                        const idToken = hashParams.get("id_token");
                        const refreshToken = hashParams.get("refresh_token");
                        if (idToken) {
                            localStorage.setItem(STORAGE_KEYS.TOKEN, idToken);
                        }
                        if (refreshToken) {
                            localStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken);
                        }
                        promotePendingOAuthFederation();
                        oauthMethod = getStoredFederation() === "apple" ? "apple" : "google";
                        window.location.hash = "";
                    }
                }
            } catch (err) {
                console.error(err);
                clearAuthStorage();
                clearPkceSession();
                Tracker.trackLogin("failure", oauthMethod, { reason_code: "token_exchange_failed" });
                dispatch({ type: "Logout", payload: null });
                return;
            }

            const tokenAfterCallback = localStorage.getItem(STORAGE_KEYS.TOKEN);
            if (
                sessionStorage.getItem(POST_LOGOUT_RETURN_SESSION_KEY) === "1" &&
                !tokenAfterCallback
            ) {
                sessionStorage.removeItem(POST_LOGOUT_RETURN_SESSION_KEY);
                dispatch({ type: "Logout", payload: null });
                return;
            }

            const accessToken = tokenAfterCallback;

            try {
                if (!accessToken) {
                    throw new Error("Token não encontrado");
                }

                await assertJwksKid(accessToken);

                setTimeout(() => {
                    try {
                        settleSession(accessToken, {
                            oauthCallback: isOAuthCallback,
                            oauthMethod,
                        });
                    } catch (err) {
                        console.error(err);
                        if (isOAuthCallback) {
                            Tracker.trackLogin("failure", oauthMethod, { reason_code: "unknown" });
                        }
                        clearAuthStorage();
                        dispatch({ type: "Logout", payload: null });
                    }
                }, isOAuthCallback ? 400 : 0);
            } catch (err) {
                console.error(err);
                if (isOAuthCallback) {
                    Tracker.trackLogin("failure", oauthMethod, { reason_code: "unknown" });
                }
                dispatch({ type: "Logout", payload: null });
            }
        };

        void validateToken();
    }, [settleSession]);

    useEffect(() => {
        const interval = setInterval(async () => {
            const idToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
            if (!idToken) {
                dispatch({ type: "Logout", payload: null });
                return null;
            }

            try {
                const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
                if (!refreshToken) {
                    return null;
                }

                setRefreshingToken(true);
                const result = (
                    await axios.get<{ data: { id_token: string } }>(
                        `${import.meta.env.VITE_API_URL}/refresh-token?refresh_token=${refreshToken}`,
                        {
                            headers: {
                                Authorization: idToken,
                            },
                        },
                    )
                ).data;

                const nextToken = result?.data.id_token ?? "";
                localStorage.setItem(STORAGE_KEYS.TOKEN, nextToken);
                if (nextToken) {
                    settleSession(nextToken);
                }

                return result;
            } catch {
                dispatch({ type: "Logout", payload: null });
            } finally {
                setRefreshingToken(false);
            }
        }, 1000 * 60 * 60);

        return () => clearInterval(interval);
    }, [settleSession]);

    const login = (token: string) => {
        try {
            const verified = assertValidIdToken(token);
            settleSession(token);
            if (isCompleteRegisterClaim(verified.complete_register) && verified.user_id) {
                void Tracker.identify(verified.user_id);
                Tracker.trackLogin("success", "password");
            }
        } catch (err) {
            console.error(err);
            clearAuthStorage();
            dispatch({ type: "Logout", payload: null });
        }
    };

    const finishRegistration = useCallback((token: string, refreshToken?: string | null) => {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        if (refreshToken) {
            localStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken);
        }
        settleSession(token);
        const user = userFromClaims(jwtDecode(token));
        if (user.user_id) {
            void Tracker.identify(user.user_id);
        }
    }, [settleSession]);

    useEffect(() => {
        if (state.isAuthenticated && state.user.user_id) {
            void Tracker.identify(state.user.user_id);
        }
    }, [state.isAuthenticated, state.user.user_id]);

    const logout = useCallback(async () => {
        Tracker.track(AnalyticsEvent.AuthLogout);
        Tracker.reset();
        flushSync(() => {
            setIsLoggingOut(true);
        });

        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
        const accessToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const federation = getStoredFederation() ?? "google";

        let hostedUrl: string | null = null;
        try {
            if (refreshToken || accessToken) {
                const result = await requestLogoutApi({
                    refreshToken,
                    accessToken,
                    federation,
                });
                hostedUrl = result.hostedUrl;
            }
        } catch {
            /* still clear local session; API may be down or tokens already invalid */
        }

        clearAuthStorage();
        clearPkceSession();
        queryClient.clear();

        try {
            if (hostedUrl) {
                window.location.assign(hostedUrl);
            } else {
                sessionStorage.setItem(POST_LOGOUT_RETURN_SESSION_KEY, "1");
                window.location.assign("/");
            }
        } catch {
            setIsLoggingOut(false);
            dispatch({ type: "Logout", payload: null });
        }
    }, [queryClient]);

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                finishRegistration,
                logout,
                refreshingToken,
                isLoggingOut,
            }}
        >
            {isLoggingOut ? <SessionLogoutOverlay /> : null}
            {children}
        </AuthContext.Provider>
    );
};

const useAuthContext = () => {
    const context = useContext(AuthContext);

    if (!context) throw new Error(`AuthContext cannot be null`);

    return context;
};

export { AuthProvider, useAuthContext };
